import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Calendar, MapPin, Users } from 'lucide-react';
import { Badge } from '../ui/badge';
import { getImageUrl as getImageUrlUtil } from '../../utils/apiUrl';

const categoryColors = {
  music: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
  comedy: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
  sports: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20',
  wedding: 'bg-pink-500/10 text-pink-500 border-pink-500/20',
  weddings: 'bg-pink-500/10 text-pink-500 border-pink-500/20',
  nightlife: 'bg-primary/10 text-primary border-primary/20',
  festivals: 'bg-rose-500/10 text-rose-500 border-rose-500/20',
  arts: 'bg-red-500/10 text-red-500 border-red-500/20',
  food: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  business: 'bg-slate-500/10 text-slate-500 border-slate-500/20',
  awards: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
  exhibition: 'bg-teal-500/10 text-teal-500 border-teal-500/20',
};

const categoryLabels = {
  music: 'Music',
  comedy: 'Comedy',
  sports: 'Sports',
  wedding: 'Wedding',
  weddings: 'Weddings',
  nightlife: 'Nightlife',
  festivals: 'Festivals',
  arts: 'Arts & Theater',
  food: 'Food & Drink',
  business: 'Business',
  awards: 'Awards',
  exhibition: 'Exhibition',
};

const CURRENCY_SYMBOLS = {
  'NGN': '₦', 'GHS': 'GH₵', 'KES': 'KSh', 'ZAR': 'R',
  'CAD': 'CA$', 'USD': '$', 'GBP': '£', 'EUR': '€'
};

export const EventCard = ({ event }) => {
  const eventDate = event.date ? new Date(event.date) : null;
  const eventEndDate = event.end_date ? new Date(event.end_date) : null;
  const isExhibition = event.category === 'exhibition';
  const lowestPrice = event.ticket_types?.length > 0 
    ? Math.min(...event.ticket_types.map(t => t.price)) 
    : 0;
  const hasFreeTIckets = event.ticket_types?.some(t => t.is_free);
  const cs = CURRENCY_SYMBOLS[event.currency] || '₦';

  // Use centralized image URL utility
  const imageUrl = getImageUrlUtil(event.image_url, 'https://images.unsplash.com/photo-1563841930606-67e2bce48b78?auto=format&fit=crop&q=80&w=800');
  
  // Determine price display based on event_type
  const getPriceDisplay = () => {
    // For recurring events, show price range starting price
    if (event.is_recurring) {
      if (event.price_range && event.price_range.min_price > 0) {
        return <span>From {cs}{event.price_range.min_price.toLocaleString()}</span>;
      } else if (event.price_range && event.price_range.max_price > 0) {
        return <span>Up to {cs}{event.price_range.max_price.toLocaleString()}</span>;
      }
      // Don't show "Free" for recurring events, just show nothing or "See Details"
      return <span className="text-muted-foreground">See Details</span>;
    }
    
    // Non-recurring events
    if (event.event_type === 'free') {
      return <span className="text-secondary">Free</span>;
    } else if (event.event_type === 'reservation') {
      return <span className="text-blue-400">RSVP Required</span>;
    } else if (hasFreeTIckets) {
      return <span className="text-secondary">Free</span>;
    } else {
      return <span>{cs}{lowestPrice.toLocaleString()}</span>;
    }
  };

  return (
    <Link 
      to={`/events/${event.event_id}`}
      className="event-card block"
      data-testid={`event-card-${event.event_id}`}
    >
      {/* Image */}
      <div className="event-image-container aspect-[16/10] relative">
        <img
          src={imageUrl}
          alt={event.title}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1563841930606-67e2bce48b78?auto=format&fit=crop&q=80&w=800';
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        
        {/* Category badge */}
        <Badge 
          className={`absolute top-3 left-3 ${categoryColors[event.category] || categoryColors.nightlife}`}
        >
          {categoryLabels[event.category] || event.category}
        </Badge>

        {/* Date badge - show for non-recurring events with date */}
        {eventDate && !event.is_recurring && (
          <div className="absolute top-3 right-3 bg-background/90 backdrop-blur-sm rounded-lg px-2 py-1 text-center">
            <div className="text-xs font-medium text-primary">
              {format(eventDate, 'MMM')}
            </div>
            <div className="text-lg font-bold leading-tight">
              {format(eventDate, 'd')}
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-lg mb-2 line-clamp-1 group-hover:text-primary transition-colors">
          {event.title}
        </h3>

        <div className="flex flex-col gap-2 text-sm text-muted-foreground mb-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 flex-shrink-0" />
            {event.is_recurring ? (
              <span>Recurring Event</span>
            ) : isExhibition && eventEndDate ? (
              <span>{format(eventDate, 'MMM d')} - {format(eventEndDate, 'MMM d')}</span>
            ) : eventDate ? (
              <span>{format(eventDate, 'EEE, MMM d • h:mm a')}</span>
            ) : (
              <span>Date TBD</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 flex-shrink-0" />
            <span className="line-clamp-1">{event.venue}, {event.city}</span>
          </div>
        </div>

        {/* Price & Organizer */}
        <div className="flex items-center justify-between pt-3 border-t border-border">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Users className="w-4 h-4" />
            <span>{event.organizer_name}</span>
          </div>
          <div className="font-mono font-medium">
            {getPriceDisplay()}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default EventCard;
